import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { LocalStorageService } from "./localstorage.service";
import { saveAs } from "file-saver";
import { timeout, retryWhen, concatMap } from "rxjs/operators";
import { throwError, timer } from "rxjs";

export interface CheckProcedaResponse {
  idCliente: number;
  conemb: boolean;
  doccob: boolean;
  notfs: boolean;
  ocoren: boolean;
}

@Injectable()
export class FinancialApiService {
  private static readonly OCOREN_CACHE_PREFIX = 'ocoren_cliente_';

  private URL: string = environment.angularJSconstants.URL_PROCEDA;

  private URL_FINANCIAL: string = environment.angularJSconstants.URL_FINANCEIRO;

  private URL_RELATORIO: string = environment.angularJSconstants.URL_RELATORIOS;

  private URL_PEDIDO: string = environment.angularJSconstants.URLAPIPEDIDO;

  private URL_PEDIDO_REPLICA: string =
    environment.angularJSconstants.URLAPIPEDIDO_REPLICA;

    private URL_DOWNLOAD_DOCUMENTO: string =
    environment.angularJSconstants.URL_SERVICO_FISCAL;

  constructor(
    private http: HttpClient,
    private localStorageService: LocalStorageService
  ) {}

  checkTypes(id: number): Promise<any> {
    return this.http
      .get<CheckProcedaResponse>(`${this.URL}/v1/cliente`, this.makeHeaders())
      .toPromise();
  }

  private createOcorenHeaders() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      Authorization: this.localStorageService.get("token"),
    });
  }

  private isClientError(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status >= 400 && error.status < 500;
  }

  private withRetryAndTimeout<T>(operation: string) {
    return (source: any) => source.pipe(
      timeout(10000),
      retryWhen(errors => errors.pipe(
        concatMap((error, index) => {
          if (this.isClientError(error) || index >= 3) {
            return throwError(error);
          }
          const delayMs = Math.pow(2, index) * 1000;
          console.warn(`Tentativa ${index + 1} falhou para ${operation}. Tentando novamente em ${delayMs}ms...`);
          return timer(delayMs);
        })
      ))
    );
  }

  async isClienteProcedaComOcorenHabilitado(clienteId: number): Promise<boolean> {
    const storageKey = FinancialApiService.OCOREN_CACHE_PREFIX + clienteId;

    const cached = sessionStorage.getItem(storageKey);
    if (cached !== null) {
      return cached === 'true';
    }

    const globalCache: Record<string, Promise<boolean>> =
      (window as any).__ocorenPromiseCache = (window as any).__ocorenPromiseCache || {};

    if (globalCache[clienteId]) {
      return globalCache[clienteId];
    }

    globalCache[clienteId] = (async () => {
      try {
        const result = await this.http
          .get<CheckProcedaResponse>(`${this.URL}/v1/ocoren/cliente/${clienteId}`, {
            headers: this.createOcorenHeaders()
          })
          .pipe(timeout(10000))
          .toPromise();

        const enabled = result && typeof result === 'object' && (result as CheckProcedaResponse).ocoren === true;
        sessionStorage.setItem(storageKey, String(enabled));
        return enabled;
      } catch (error) {
        console.error('Erro ao verificar cliente OCOREN:', error);
        sessionStorage.setItem(storageKey, 'false');
        return false;
      }
    })();

    return globalCache[clienteId];
  }

  async gerarOcoren(payload: { idCliente: number; codigoRastreamentoRemessasLista: string[] }): Promise<any> {
    try {
      return await this.http
        .post(`${this.URL}/v1/ocoren/push/eventosProceda`, payload, {
          headers: this.createOcorenHeaders()
        })
        .pipe(this.withRetryAndTimeout(`gerar OCOREN cliente ${payload.idCliente}`))
        .toPromise();
    } catch (error) {
      console.error('Erro ao gerar OCOREN:', error);
      throw error;
    }
  }

  public async getDoccob(idFatura: number): Promise<any> {
    return this.http
      .get(`${this.URL}/v1/doccob/${idFatura}`, this.makeHeaders())
      .toPromise();
  }

  public async filtrosReembolso(idUser: number, filtros: any) {
    const url = `${this.URL_FINANCIAL}/v2/historico-reembolso${this.montarQueryReembolso(
      filtros
    )}`;

    return this.http
      .get(url, this.makeHeaders())
      .toPromise();
  }

  private montarQueryReembolso(filtros: any): string {
    const params: string[] = [];

    const adicionar = (chave: string, valor: any) => {
      if (valor === undefined || valor === null || valor === "") {
        return;
      }

      params.push(`${chave}=${encodeURIComponent(valor)}`);
    };

    adicionar("limit", filtros.limit);
    adicionar("offset", filtros.offset);
    adicionar("rastreamento", filtros.rastreamento);
    adicionar("nomeDestinatario", filtros.nomeDestinatario);
    adicionar("dataOcorrenciaInicial", filtros.dataOcorrenciaInicial);
    adicionar("dataOcorrenciaFinal", filtros.dataOcorrenciaFinal);
    adicionar("sort", filtros.sort);

    if (filtros.tipo === "reembolsado" || filtros.tipo === "previsto") {
      adicionar("reembolsado", filtros.tipo === "reembolsado");
    } else {
      adicionar("reembolsado", filtros.reembolsado);
    }

    const motivos = Array.isArray(filtros.motivo)
      ? filtros.motivo
      : filtros.motivo
      ? [filtros.motivo]
      : [];

    motivos.forEach((motivo: string) => adicionar("motivo", motivo));

    return params.length ? `?${params.join("&")}` : "";
  }

  public async filtrosSituacaoEncomenda(
    idUser: number,
    filtros: any,
    filterColumns: any
  ) {
    return this.http
      .get(
        `${this.URL_PEDIDO_REPLICA}/v1/encomendas/historico?limit=${filtros.limit}&offset=${filtros.offset}&${filterColumns}`,
        this.makeHeaders()
      )
      .toPromise();
  }

  public async getConemb(idFatura: number): Promise<any> {
    return this.http
      .get(
        `${this.URL}/v1/conemb/fatura/gerar-link-download/${idFatura}`,
        this.makeHeaders()
      )
      .toPromise();
  }

  public async getDocumentoFiscal(tipo, fatura): Promise<any> {
    return this.http
      .post(
        `${this.URL_DOWNLOAD_DOCUMENTO}/v2/faturas/${fatura}/gerar-url-zip?extensao=${tipo}`,
        {},
        {
          responseType: 'json',
          headers: new HttpHeaders({
            Authorization: this.localStorageService.get("token"),
          }),
        }
      )
      .toPromise();
  }

  public downloadRelatorio(idUser: number, filtros: any, tipo: string) {
    let url = null;
    let originUrl = null;
    switch (tipo) {
      case "devolucoes":
        originUrl = this.URL_RELATORIO;
        url = `/devolucoes/relatorio?${filtros}`;
        break;
      case "encomenda":
        originUrl = this.URL_RELATORIO;
        url = `/historico/encomendas/relatorio?clienteId=${idUser}&${filtros}`;
        break;
      case "reembolso":
        originUrl = this.URL_FINANCIAL;
        url = `/v1/historico-reembolso/relatorio?${filtros}`;
        break;
    }

    if (!url || !originUrl) {
      return;
    }

    return this.http
      .get(`${originUrl}/${url}`, {
        responseType: "blob",
        headers: new HttpHeaders({
          Authorization: this.localStorageService.get("token"),
        }),
      })
      .subscribe((response) => {
        const blob = new Blob([response], {
          type: "application/vnd.ms.excel",
        });
        const file = new File([blob], `relatorio-${tipo}.xlsx`, {
          type: "application/vnd.ms.excel",
        });

        saveAs(file);
      });
    // .toPromise();
  }

  private makeHeaders() {
    const headers = new HttpHeaders({
      Authorization: this.localStorageService.get("token"),
    });

    return {
      headers,
      responseType: "text" as "json",
    };
  }
}
